'use client'

import { useEffect, useState } from 'react'
import { useConvexAuth, useQuery } from 'convex/react'
import { useTranslations } from 'next-intl'
import { Ban, X } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import { useDateFormatter } from '@/hooks/useDateFormatter'

const SESSION_STORAGE_KEY = 'outOfRunsBannerDismissed'

/**
 * Dismissible informational banner for two distinct monthly-run states, both
 * deliberately neutral/amber (the org is NOT suspended):
 *
 *  - `exhausted` — the org has used its whole budget (`hasBudget === false`).
 *  - `skipped`  — an analysis was skipped because it needs more runs than the
 *    org has left, even though some budget remains (`recentlySkipped === true`).
 *    Without this the analysis would stop running with only a per-job chip and
 *    no global notice, while the meter still shows runs available.
 *
 * Exhausted takes precedence. Each state has its own sessionStorage dismissal
 * key so dismissing one doesn't hide the other. Modeled on `TrialBanner`.
 *
 * Gated on `useConvexAuth().isAuthenticated` (not Clerk `isSignedIn`) per the
 * Frontend Auth Rules in CLAUDE.md.
 */
export default function OutOfRunsBanner() {
  const t = useTranslations('billing.outOfRunsBanner')
  const df = useDateFormatter()
  const { isAuthenticated } = useConvexAuth()
  const [dismissed, setDismissed] = useState(true)

  const organizations = useQuery(api.organizations.getOrganizations, isAuthenticated ? {} : 'skip')
  const organizationId = organizations?.[0]?._id

  const usage = useQuery(
    api.runLimits.getRunUsage,
    organizationId ? { organizationId } : 'skip',
  )

  // Which signal applies, if any. Exhausted wins over skipped.
  const mode: 'exhausted' | 'skipped' | null =
    !usage || usage.unlimited
      ? null
      : !usage.hasBudget
        ? 'exhausted'
        : usage.recentlySkipped
          ? 'skipped'
          : null

  useEffect(() => {
    if (!mode) return
    const stored =
      typeof window !== 'undefined' ? sessionStorage.getItem(`${SESSION_STORAGE_KEY}:${mode}`) : null
    setDismissed(stored === '1')
  }, [mode])

  if (!usage || !mode || dismissed) return null

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(`${SESSION_STORAGE_KEY}:${mode}`, '1')
    } catch {
      // sessionStorage unavailable (private mode, etc.) — dismiss for this render only.
    }
    setDismissed(true)
  }

  // resetsAt is a UTC-midnight instant; format in UTC so users west of UTC
  // don't see the previous day (matches the backend emails / error messages).
  const resetDate = df.date(usage.resetsAt, { timeZone: 'UTC' })
  const message =
    mode === 'exhausted'
      ? t('message', { date: resetDate })
      : t('skippedMessage', { date: resetDate, remaining: usage.remaining ?? 0 })

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-3 px-4 py-2.5 text-sm relative"
      style={{
        background: 'var(--color-orange-muted)',
        borderBottom: '1px solid var(--color-border)',
        color: 'var(--color-text-secondary)',
      }}
      role="status"
      aria-live="polite"
    >
      <Ban className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-orange)' }} />
      <span className="text-center" style={{ color: 'var(--color-text-primary)' }}>
        {message}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('dismiss')}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:opacity-70 transition-opacity"
        style={{ color: 'var(--color-text-muted)' }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
